import { lazy, Suspense, useEffect } from 'react';
import { HashRouter, Navigate, Route, Routes } from 'react-router-dom';
import { HomeScreen } from '@/features/home/HomeScreen';

// Home loads with the app; every other screen is its own chunk, fetched on first visit
// (and cached by the service worker for offline use).
const SetupScreen = lazy(() =>
  import('@/features/setup/SetupScreen').then((m) => ({ default: m.SetupScreen })),
);
const ScoreboardScreen = lazy(() =>
  import('@/features/game/ScoreboardScreen').then((m) => ({ default: m.ScoreboardScreen })),
);
const ReportScreen = lazy(() =>
  import('@/features/report/ReportScreen').then((m) => ({ default: m.ReportScreen })),
);
const HistoryScreen = lazy(() =>
  import('@/features/history/HistoryScreen').then((m) => ({ default: m.HistoryScreen })),
);
const RulesScreen = lazy(() =>
  import('@/features/rules/RulesScreen').then((m) => ({ default: m.RulesScreen })),
);
const TeamsScreen = lazy(() =>
  import('@/features/teams/TeamsScreen').then((m) => ({ default: m.TeamsScreen })),
);
const PlayersScreen = lazy(() =>
  import('@/features/teams/PlayersScreen').then((m) => ({ default: m.PlayersScreen })),
);
const TeamListsScreen = lazy(() =>
  import('@/features/teams/TeamListsScreen').then((m) => ({ default: m.TeamListsScreen })),
);
const SettingsScreen = lazy(() =>
  import('@/features/settings/SettingsScreen').then((m) => ({ default: m.SettingsScreen })),
);
import { applyTheme, useSettings } from '@/features/settings/settingsStore';
import { setLanguage } from '@/i18n';
import { unlockAudio } from '@/platform/feedback';
import { Toaster } from '@/ui';

/** Route paths, shared by all screens. Hash routing works in the PWA and in Capacitor. */
export const ROUTES = {
  home: '/',
  setup: '/new',
  game: (id: string) => `/game/${id}`,
  report: (id: string) => `/report/${id}`,
  history: '/history',
  settings: '/settings',
  teams: '/teams',
  teamLists: '/teams/lists',
  players: (teamId: string) => `/teams/${teamId}`,
  rules: '/rules',
} as const;

export function App() {
  const theme = useSettings((s) => s.theme);
  const language = useSettings((s) => s.language);

  useEffect(() => applyTheme(theme), [theme]);
  useEffect(() => {
    void setLanguage(language);
  }, [language]);
  useEffect(() => {
    const unlock = () => unlockAudio();
    window.addEventListener('pointerdown', unlock, { passive: true });
    return () => window.removeEventListener('pointerdown', unlock);
  }, []);

  return (
    <HashRouter>
      <Suspense fallback={<div className="min-h-dvh bg-bg" aria-busy="true" />}>
        <Routes>
          <Route path="/" element={<HomeScreen />} />
          <Route path="/new" element={<SetupScreen />} />
          <Route path="/game/:id" element={<ScoreboardScreen />} />
          <Route path="/report/:id" element={<ReportScreen />} />
          <Route path="/history" element={<HistoryScreen />} />
          <Route path="/settings" element={<SettingsScreen />} />
          <Route path="/teams" element={<TeamsScreen />} />
          <Route path="/teams/lists" element={<TeamListsScreen />} />
          <Route path="/teams/:id" element={<PlayersScreen />} />
          <Route path="/rules" element={<RulesScreen />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <Toaster />
    </HashRouter>
  );
}
